import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { UsePaginationState } from '@uhg-abyss/web/hooks/usePagination';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { isNil } from 'lodash';
import React, { ReactNode } from 'react';

import { ResultsCount } from '../ResultsCount';
import { NumberedPagination } from './components/NumberedPagination';
import { Styles } from './includes/styles';

type FooterProps = {
  footerBottom?: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  footerTop?: ReactNode;
  pagination: UsePaginationState;
  resultsPerPage?: number;
  showNumberedPagination?: boolean;
  showResultsCount?: boolean;
  totalResults?: number;
};

/**
 * Footer
 *
 * Displays components below the table which are used to control the data within the table.
 */
export function Footer({
  footerBottom = null,
  footerLeft = null,
  footerRight = null,
  footerTop = null,
  pagination = {
    canNextPage: false,
    canPreviousPage: false,
    firstPage(): void {},
    goToPage(pageNumber: number): void {},
    gotoPage(pageNumber: number): void {},
    lastPage(): void {},
    nextPage(): void {},
    pageCount: 0,
    pageIndex: 0,
    previousPage(): void {},
    setData(value: React.SetStateAction<object[]>): void {},
    state: {
      currentPage: 0,
      pageCount: 0,
      pageIndex: 0,
      rowCount: 0,
      rows: [],
    },
  },
  resultsPerPage = 25,
  showNumberedPagination = true,
  showResultsCount = true,
  totalResults = 0,
}: FooterProps) {
  return (
    <ErrorHandler location="src/shared/ui/components/TableQuery/components/Footer/Footer.tsx">
      <Styles>
        <Grid className="tableFooter">
          {!isNil(footerTop) && (
            <Grid.Col className="top" span={{ lg: '100%', md: '100%', sm: '100%', xs: '100%' }}>
              {footerTop}
            </Grid.Col>
          )}
          <Grid.Col className="left" span={{ lg: '50%', md: '50%', sm: '100%', xs: '100%' }}>
            {!isNil(footerLeft) ? (
              footerLeft
            ) : (
              <React.Fragment>
                {showResultsCount && (
                  <ResultsCount
                    currentPage={pagination.state.pageIndex}
                    resultsPerPage={resultsPerPage}
                    totalResults={totalResults}
                  />
                )}
              </React.Fragment>
            )}
          </Grid.Col>
          <Grid.Col className="right" span={{ lg: '50%', md: '50%', sm: '100%', xs: '100%' }}>
            {!isNil(footerRight) ? (
              footerRight
            ) : (
              <React.Fragment>
                {showNumberedPagination && (
                  <Flex alignContent="flex-start" alignItems="flex-start" direction="row" justify="flex-end">
                    <NumberedPagination {...pagination} />
                  </Flex>
                )}
              </React.Fragment>
            )}
          </Grid.Col>
          {!isNil(footerBottom) && (
            <Grid.Col className="bottom" span={{ lg: '100%', md: '100%', sm: '100%', xs: '100%' }}>
              {footerBottom}
            </Grid.Col>
          )}
        </Grid>
      </Styles>
    </ErrorHandler>
  );
}
